import InfoIcon from "@mui/icons-material/Info";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import TuneIcon from "@mui/icons-material/Tune";
import {
	Box,
	Chip,
	Divider,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Tooltip,
	Typography
} from "@mui/material";
import { CommunicationPreferenceItem } from "models/communicationPreference";

function channelSummary(preference: CommunicationPreferenceItem) {
	const channels = [
		preference.portal && "Portal",
		preference.email && "Email",
		preference.sms && "SMS"
	].filter(Boolean);

	return channels.length > 0 ? channels.join(" + ") : "Muted";
}

export default function CommunicationsCell({
	preferences
}: {
	preferences: CommunicationPreferenceItem[];
}) {
	if (preferences.length === 0) {
		return <Typography variant="text14">-</Typography>;
	}

	const isEnabled = preferences.some((p) => p.portal || p.email || p.sms);
	const emailUsed = preferences.some((p) => p.email);
	const smsUsed = preferences.some((p) => p.sms);

	const chipSx = (used: boolean) => ({
		typography: "text12Bold",
		bgcolor: used ? "primary.main" : "action.disabledBackground",
		color: used ? "primary.contrastText" : "text.disabled"
	});

	const tooltipContent = (
		<Box sx={{ minWidth: { xs: 260, sm: 400 }, p: 1 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
				<TuneIcon sx={{ fontSize: 16, color: "orange.main", transform: "rotate(90deg)" }} />
				<Typography variant="text14Bold" sx={{ color: "orange.main", letterSpacing: 0.5 }}>
					{isEnabled ? "ENABLED COMMUNICATIONS" : "MUTED COMMUNICATIONS"}
				</Typography>
			</Box>
			<Divider sx={{ borderColor: "grey.700", mb: 1.5 }} />
			<Table
				size="small"
				sx={{
					"& .MuiTableCell-root": {
						border: 0,
						py: 0.75,
						px: 0
					}
				}}
			>
				<TableBody>
					{preferences.map((p) => (
						<TableRow key={p.notificationTypeId}>
							<TableCell
								sx={{
									color: "rgba(255, 255, 255, 0.7)",
									width: "1%",
									whiteSpace: "nowrap",
									pr: 3
								}}
							>
								{p.name}:
							</TableCell>
							<TableCell
								align="right"
								sx={{ color: "common.white", fontWeight: "bold" }}
							>
								{channelSummary(p)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</Box>
	);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			{isEnabled ? (
				<NotificationsIcon fontSize="small" sx={{ color: "text.primary" }} />
			) : (
				<NotificationsNoneIcon fontSize="small" sx={{ color: "text.disabled" }} />
			)}
			<Typography
				variant="text14"
				sx={isEnabled ? undefined : { color: "text.disabled", textDecoration: "line-through" }}
			>
				{isEnabled ? "Enabled" : "Muted"}
			</Typography>
			{isEnabled && (
				<>
					<Chip label="Email" size="small" variant="filled" sx={chipSx(emailUsed)} />
					<Chip label="SMS" size="small" variant="filled" sx={chipSx(smsUsed)} />
				</>
			)}
			<Tooltip
				title={tooltipContent}
				arrow
				enterTouchDelay={0}
				slotProps={{ tooltip: { sx: { maxWidth: 480 } } }}
			>
				<InfoIcon
					fontSize="small"
					sx={{
						color: "text.secondary",
						cursor: "pointer",
						fontSize: 16,
						ml: "auto"
					}}
				/>
			</Tooltip>
		</Box>
	);
}
